import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import MemberHeader from "@/components/MemberHeader";
import AuthImage from "@/components/AuthImage";
import { ChangePasswordForm } from "@/components/ChangePasswordForm";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Camera, ArrowLeft, BadgeCheck } from "lucide-react";

export default function Profile() {
  const { user, refresh, setUser } = useAuth();
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    name: user?.name || "", birth_date: user?.birth_date || "",
    profession: user?.profession || "", motivation: user?.motivation || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/profile", form);
      await refresh();
      toast.success("Perfil atualizado!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Fotografia atualizada!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const deactivate = async () => {
    setCancelling(true);
    try {
      await api.post("/profile/deactivate");
      setUser(false);
      toast.success("A tua subscrição foi cancelada.");
      window.location.replace("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setCancelling(false);
    }
  };

  return (
    <div className="min-h-screen sc-paper">
      <MemberHeader />
      <main className="max-w-2xl mx-auto px-4 sm:px-8 py-12">
        <Link to="/area" className="inline-flex items-center gap-2 sc-label text-[#6E5F58] hover:text-[#48020C]"><ArrowLeft className="w-4 h-4" /> Voltar</Link>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#26130E] mt-6">O meu perfil</h1>

        <div className="flex items-center gap-6 mt-10">
          <div className="relative">
            <AuthImage path={user?.photo_path} fallbackName={user?.name} className="w-28 h-28 rounded-full object-cover border-2 border-[#48020C] text-2xl" />
            <button data-testid="upload-photo-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="absolute -bottom-1 -right-1 bg-[#48020C] hover:bg-[#26130E] text-[#FCFCFA] rounded-full w-10 h-10 flex items-center justify-center transition-colors">
              <Camera className="w-4 h-4" />
            </button>
            <input ref={fileRef} data-testid="photo-input" type="file" accept="image/*" onChange={upload} className="hidden" />
          </div>
          <div>
            <div className="font-serif text-2xl text-[#26130E]">{user?.name}</div>
            {user?.is_founding && (
              <span className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-[#8A5B00] bg-[#FEF7E7] px-3 py-1 rounded-full">
                <BadgeCheck className="w-3.5 h-3.5" /> Membro Fundadora
              </span>
            )}
            <p className="text-sm text-[#6E5F58] mt-2">{uploading ? "A carregar foto…" : "Clica no ícone para mudar a foto"}</p>
          </div>
        </div>

        <form onSubmit={save} className="mt-10 space-y-6 bg-[#F4F1EA] border border-[#E3DED2] rounded-2xl p-6 sm:p-8">
          <div className="space-y-2"><Label className="sc-label">Nome</Label><Input data-testid="profile-name" value={form.name} onChange={set("name")} className="sc-input3" /></div>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-2"><Label className="sc-label">Data de nascimento</Label><Input data-testid="profile-birthdate" type="date" max="2026-12-31" value={form.birth_date} onChange={set("birth_date")} className="sc-input3" /></div>
            <div className="space-y-2"><Label className="sc-label">Profissão</Label><Input data-testid="profile-profession" value={form.profession} onChange={set("profession")} className="sc-input3" /></div>
          </div>
          <div className="space-y-2"><Label className="sc-label">Sobre mim</Label><Textarea data-testid="profile-motivation" value={form.motivation} onChange={set("motivation")} className="sc-input3 min-h-24" /></div>
          <button type="submit" disabled={saving} data-testid="save-profile-btn" className="bg-[#48020C] hover:bg-[#26130E] text-[#FCFCFA] rounded-full px-8 py-3 transition-colors">
            {saving ? "A guardar…" : "Guardar alterações"}
          </button>
        </form>

        <div className="mt-10 border-t border-[#E3DED2] pt-8">
          <h2 className="font-serif text-2xl text-[#26130E] mb-5">Alterar password</h2>
          <ChangePasswordForm />
        </div>

        <div className="mt-10 border-t border-[#E3DED2] pt-8">
          <h2 className="font-serif text-2xl text-[#26130E]">Cancelar subscrição</h2>
          <p className="text-sm text-[#6E5F58] mt-2 max-w-lg">
            Ao cancelar deixas de ser membro e perdes o acesso à plataforma com este email e palavra-passe.
            As tuas inscrições em planos são removidas. Esta ação não pode ser revertida por ti.
          </p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid="deactivate-account-btn" className="mt-5 border border-[#8C2222] text-[#8C2222] hover:bg-[#8C2222] hover:text-[#FCFCFA] rounded-full px-7 py-2.5 text-sm transition-colors">
                Cancelar subscrição
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="deactivate-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-serif">Tens a certeza?</AlertDialogTitle>
                <AlertDialogDescription>
                  Vais perder o acesso à plataforma com este email e palavra-passe, e serás removida de todos os planos em que estás inscrita. Esta ação é definitiva.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="deactivate-cancel-btn">Manter conta</AlertDialogCancel>
                <AlertDialogAction data-testid="deactivate-confirm-btn" disabled={cancelling} onClick={deactivate} className="bg-[#8C2222] hover:bg-[#6E1818]">
                  {cancelling ? "A cancelar…" : "Sim, cancelar subscrição"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </main>
      <style>{`.sc-input3{background:#FCFCFA;border-color:#E3DED2;border-radius:0.6rem;height:auto;padding:0.65rem 0.85rem;}`}</style>
    </div>
  );
}
